/**
 * @file Reports.tsx
 * @description 
 * Advanced Reporting and Analytics module for the CRM dashboard.
 * Fetches contact data from Strapi v5, aggregates it based on time (months) and type (kind), 
 * and visualizes the trends using Recharts.
 * 
 * Features:
 * - Dynamic Time Filtering: View data for the last 3, 6, or 12 months.
 * - Categorical Filtering: Toggle visibility of specific contact types ("Osoba", "Společnost", "Lead").
 * - Responsive Charts: Uses Recharts ResponsiveContainer to ensure perfect rendering on mobile and desktop.
 * - Strapi v5 Integration: Utilizes advanced filtering ($gte on createdAt) to minimize data payload.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
    Legend, ResponsiveContainer, PieChart, Pie, Cell 
} from "recharts";
import {
    Filter, Loader2, PieChart as PieChartIcon, AlertTriangle,
    TrendingUp, User, Target, Building2
} from "lucide-react";
import { Contact, UserProfile } from "../../types/objects";
import { fetchAll, toUserMessage } from "../../api/strapi";
import { MainLayout, ErrorBanner } from "../ui/components";

/** Safety limit of contacts loaded into the browser for one report. */
const REPORT_MAX_ITEMS = 5000;

// Define the available contact kinds mapped from the Strapi schema
type ContactKind = "Osoba" | "Společnost" | "Lead";
const ALL_KINDS: ContactKind[] = ["Osoba", "Společnost", "Lead"];

// Theme colors for consistent chart rendering matching the CRM UI
const CHART_COLORS = {
    "Osoba": "#3b82f6",      // Blue
    "Společnost": "#f97316", // Orange
    "Lead": "#10b981"        // Green
};

export default function Reports({ user: _user }: { user: UserProfile }) {
    // --- STATE MANAGEMENT ---
    
    // UI Control States
    const [monthsCount, setMonthsCount] = useState<number>(6);
    const [selectedKinds, setSelectedKinds] = useState<ContactKind[]>([...ALL_KINDS]);
    
    // Data States
    const [loading, setLoading] = useState<boolean>(true);
    const [rawData, setRawData] = useState<any[]>([]);
    const [isTruncated, setIsTruncated] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // --- HELPER FUNCTIONS ---

    /**
     * Generates an array of month identifiers (e.g., "2023-10") for the last X months.
     * Used to initialize the data structure so months with zero data still appear on the chart.
     */
    const generateMonthBuckets = useCallback((count: number) => {
        const buckets: { label: string; dateStr: string; sortKey: string }[] = [];
        const now = new Date();
        
        for (let i = count - 1; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthName = d.toLocaleString('cs-CZ', { month: 'short' });
            buckets.push({
                label: `${monthName} ${d.getFullYear()}`,
                dateStr: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
                sortKey: d.toISOString()
            });
        }
        return buckets;
    }, []);

    // --- API OPERATIONS ---

    /**
     * Fetches contacts created within the selected time window.
     * Uses Strapi v5 filtering to request only records created after the calculated cutoff date.
     */
    const fetchReportData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Start of the month, (monthsCount - 1) months ago
            const cutoffDate = new Date();
            cutoffDate.setDate(1);
            cutoffDate.setMonth(cutoffDate.getMonth() - (monthsCount - 1));
            cutoffDate.setHours(0, 0, 0, 0);

            // The hub caps pageSize at 100, so all pages are loaded one by one.
            const { items, truncated } = await fetchAll<Contact>(
                "contacts",
                { createdFrom: cutoffDate.toISOString(), sort: "createdAt:asc" },
                REPORT_MAX_ITEMS
            );
            setRawData(items);
            setIsTruncated(truncated);
        } catch (err) {
            console.error("[reports] Failed to fetch report data", err);
            setError(toUserMessage(err, "Data pro reporty se nepodařilo načíst."));
        } finally {
            setLoading(false);
        }
    }, [monthsCount]);

    // Trigger data fetch when the selected time range changes
    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    // --- DATA TRANSFORMATION ---

    /**
     * Memoized calculation that transforms the flat array of Strapi records into 
     * the structured format required by Recharts, applying active category filters.
     */
    const chartData = useMemo(() => {
        // 1. Initialize empty buckets for the chart X-Axis
        const buckets = generateMonthBuckets(monthsCount);
        const dataMap: Record<string, any> = {};
        
        buckets.forEach(b => {
            dataMap[b.dateStr] = { 
                name: b.label.toUpperCase(), 
                "Osoba": 0, 
                "Společnost": 0, 
                "Lead": 0 
            };
        });

        // 2. Populate buckets with raw data
        rawData.forEach(contact => {
            const kind = contact.kind as ContactKind;
            
            // Skip if user filtered out this kind
            if (!selectedKinds.includes(kind)) return;

            const date = new Date(contact.createdAt);
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            
            if (dataMap[dateStr] && dataMap[dateStr][kind] !== undefined) {
                dataMap[dateStr][kind] += 1;
            }
        });

        return Object.values(dataMap);
    }, [rawData, monthsCount, selectedKinds, generateMonthBuckets]);

    /**
     * Aggregated data for the Pie Chart and Summary Cards.
     */
    const aggregateData = useMemo(() => {
        const totals = { "Osoba": 0, "Společnost": 0, "Lead": 0, total: 0 };
        
        rawData.forEach(contact => {
            const kind = contact.kind as ContactKind;
            if (selectedKinds.includes(kind) && totals[kind] !== undefined) {
                totals[kind] += 1;
                totals.total += 1;
            }
        });

        return [
            { name: "Osoba", value: totals["Osoba"] },
            { name: "Společnost", value: totals["Společnost"] },
            { name: "Lead", value: totals["Lead"] }
        ].filter(item => item.value > 0); // Remove empty slices from pie chart
    }, [rawData, selectedKinds]);

    // --- EVENT HANDLERS ---

    const toggleKindFilter = (kind: ContactKind) => {
        setSelectedKinds(prev => 
            prev.includes(kind) 
                ? prev.filter(k => k !== kind) 
                : [...prev, kind]
        );
    };

    // --- RENDER ---

    return (
        <MainLayout
            title="Reporty"
            subtitle="Přehled akvizice a struktury kontaktů v CRM."
            actions={
                <div className="flex items-center bg-slate-100 p-1.5 rounded-2xl w-full sm:w-auto font-black uppercase text-[10px]" role="group" aria-label="Období">
                    {[3, 6, 12].map(months => (
                        <button
                            key={months}
                            type="button"
                            onClick={() => setMonthsCount(months)}
                            aria-pressed={monthsCount === months}
                            className={`flex-1 sm:flex-none px-6 py-2.5 rounded-xl transition-all ${
                                monthsCount === months
                                    ? "bg-white text-blue-600 shadow-sm"
                                    : "text-slate-500 hover:text-slate-700"
                            }`}
                        >
                            {months} měsíců
                        </button>
                    ))}
                </div>
            }
        >
        <div className="space-y-6">

            {/* 2. Dynamic Control Panel (Checkboxes) */}
            <div className="bg-slate-900 p-6 rounded-[2rem] shadow-lg flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-white font-black uppercase text-xs tracking-widest">
                    <Filter size={18} className="text-blue-400" />
                    Zobrazit typy:
                </div>
                
                <div className="flex flex-wrap gap-3 w-full sm:w-auto">
                    {ALL_KINDS.map(kind => {
                        const isActive = selectedKinds.includes(kind);
                        const icons = {
                            "Osoba": <User size={16} />,
                            "Společnost": <Building2 size={16} />,
                            "Lead": <Target size={16} />
                        };
                        
                        return (
                            <button
                                key={kind}
                                onClick={() => toggleKindFilter(kind)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                                    isActive 
                                        ? "bg-white text-slate-900 shadow-md" 
                                        : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                                }`}
                            >
                                <span style={{ color: isActive ? CHART_COLORS[kind] : 'inherit' }}>
                                    {icons[kind]}
                                </span>
                                {kind}
                            </button>
                        );
                    })}
                </div>
            </div>

            {error && <ErrorBanner message={error} onRetry={fetchReportData} />}

            {/* Main Content Area */}
            {loading ? (
                <div className="py-32 flex flex-col items-center justify-center text-blue-600 uppercase font-black tracking-widest">
                    <Loader2 className="animate-spin mb-4" size={48} />
                    Generuji reporty...
                </div>
            ) : (
                <div className="space-y-6">

                    {/* Truncation Warning: shown when more records exist than the report fetched */}
                    {isTruncated && (
                        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-700 px-6 py-4 rounded-2xl text-sm font-bold">
                            <AlertTriangle size={20} className="shrink-0" />
                            Ve vybraném období je více kontaktů, než kolik report zobrazuje (limit {REPORT_MAX_ITEMS} záznamů). Data v grafech mohou být neúplná.
                        </div>
                    )}

                    {/* 3. Metrics Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col justify-center">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Celkem záznamů</span>
                            <span className="text-4xl font-black text-slate-900">{aggregateData.reduce((acc, curr) => acc + curr.value, 0)}</span>
                        </div>
                        {aggregateData.map(item => (
                            <div key={item.name} className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col justify-center border-l-4" style={{ borderLeftColor: CHART_COLORS[item.name as ContactKind] }}>
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{item.name}</span>
                                <span className="text-3xl font-black text-slate-900">{item.value}</span>
                            </div>
                        ))}
                    </div>

                    {/* 4. Charts Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        
                        {/* Primary Timeline Chart (Bar) */}
                        <div className="lg:col-span-2 bg-white p-6 md:p-8 rounded-[2rem] border border-slate-200 shadow-sm">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl"><TrendingUp size={24} /></div>
                                <div>
                                    <h3 className="font-black uppercase text-slate-900 tracking-tight">Vývoj akvizice v čase</h3>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Počet nových kontaktů po měsících</p>
                                </div>
                            </div>
                            
                            <div className="h-[350px] w-full font-bold text-xs uppercase">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8' }} dy={10} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8' }} />
                                        <RechartsTooltip 
                                            cursor={{ fill: '#f1f5f9' }}
                                            contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', fontWeight: 900, textTransform: 'uppercase' }}
                                        />
                                        <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px', fontSize: '10px', letterSpacing: '0.1em' }} />
                                        
                                        {selectedKinds.includes("Osoba") && <Bar dataKey="Osoba" stackId="a" fill={CHART_COLORS["Osoba"]} radius={[0, 0, 4, 4]} />}
                                        {selectedKinds.includes("Společnost") && <Bar dataKey="Společnost" stackId="a" fill={CHART_COLORS["Společnost"]} />}
                                        {selectedKinds.includes("Lead") && <Bar dataKey="Lead" stackId="a" fill={CHART_COLORS["Lead"]} radius={[4, 4, 0, 0]} />}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Secondary Distribution Chart (Pie) */}
                        <div className="bg-white p-6 md:p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="p-3 bg-orange-50 text-orange-500 rounded-2xl"><PieChartIcon size={24} /></div>
                                <div>
                                    <h3 className="font-black uppercase text-slate-900 tracking-tight">Struktura dat</h3>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Celkový podíl typů</p>
                                </div>
                            </div>

                            <div className="flex-1 min-h-[250px] w-full flex items-center justify-center font-bold text-xs uppercase">
                                {aggregateData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={aggregateData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={100}
                                                paddingAngle={5}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {aggregateData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={CHART_COLORS[entry.name as ContactKind]} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip 
                                                contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', fontWeight: 900, textTransform: 'uppercase' }}
                                                itemStyle={{ color: '#0f172a' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="text-center text-slate-400">
                                        <div className="mb-2 opacity-50 flex justify-center"><PieChartIcon size={48} /></div>
                                        Žádná data pro vybrané filtry
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
        </MainLayout>
    );
}
